import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import { propEq } from 'ramda';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';
import { FORMULA_COMPILE_SUCCESS_MSG } from 'modules/constants/common/all';
import * as opDefSelectors from 'modules/opDefs/data/reducer';
import * as entities from 'modules/processes/data/entities';

import { formatTimestamp } from 'utils/dateUtils';

import { types } from './actions';
import {
  addOnSchema,
  processSchema,
  profitItemSchema,
  discountSchema,
} from './entities';

// import type { Process } from './entities';

// type State = {
//   entities: Entities,
//   processId: ?Id,
//   addAddOns: {
//     addOnSearchResultsIds: array,
//     isModalOpen: boolean,
//     isSearchingAddOns: boolean,
//     isSubmitting: boolean,
//   },
//   addProfitItems: {
//     profitItemSearchResultsIds: array,
//     isModalOpen: boolean,
//     isSearchingProfitItems: boolean,
//     isSubmitting: boolean,
//   },
//   addOperations: {
//     isModalOpen: boolean,
//     isFetching: boolean,
//     type: 'operations' | 'finishes',
//   },
//   addMaterials: {
//     isModalOpen: boolean,
//     isSubmitting: boolean,
//   },
//   formulaEditor: {
//     isModalOpen: boolean,
//     isUpdating: boolean,
//     isCompiling: boolean,
//     message: ?string,
//     error: boolean,
//   },
// };

export const initialState = {
  entities: {},
  processId: null,
  addAddOns: {
    addOnSearchResultsIds: [],
    isModalOpen: false,
    isSearchingAddOns: false,
    isSubmitting: false,
  },
  availableAddOnIds: [],
  addProfitItems: {
    profitItemSearchResultsIds: [],
    isModalOpen: false,
    isSearchingProfitItems: false,
    isSubmitting: false,
  },
  availableProfitItemIds: [],
  addDiscounts: {
    discountSearchResultsIds: [],
    isModalOpen: false,
    isSearchingDiscounts: false,
    isSubmitting: false,
  },
  availableDiscountIds: [],
  addOperations: {
    isModalOpen: false,
    isFetching: false,
    type: 'operations',
  },
  addMaterials: {
    isModalOpen: false,
    isSubmitting: false,
  },
  formulaEditor: {
    isModalOpen: false,
    isUpdating: false,
    isCompiling: false,
    message: null,
    error: false,
  },
};

const entitiesReducer = createEntitiesReducer(
  [
    'addOns',
    'profitItems',
    'discounts',
    'materials',
    'finishes',
    'processMaterials',
    'processFinishes',
    'processes',
  ],
  [
    types.INIT,
    types.UPDATE_PROCESS_SUCCESS,
    types.CREATE_MATERIAL_SUCCESS,
    types.UPDATE_MATERIAL_SUCCESS,
    types.CREATE_FINISH_SUCCESS,
    types.UPDATE_FINISH_SUCCESS,
    types.ADD_OPERATIONS_SUCCESS,
    types.UPDATE_OP_FACTORY_SUCCESS,
    types.ADD_MATERIALS_SUCCESS,
    types.LOAD_ADD_ONS_SUCCESS,
    types.SEARCH_ADD_ONS_SUCCESS,
    types.ADD_ADD_ONS_SUCCESS,
    types.LOAD_PROFIT_ITEMS_SUCCESS,
    types.SEARCH_PROFIT_ITEMS_SUCCESS,
    types.ADD_PROFIT_ITEMS_SUCCESS,
    types.LOAD_DISCOUNTS_SUCCESS,
    types.SEARCH_DISCOUNTS_SUCCESS,
    types.ADD_DISCOUNTS_SUCCESS,
  ],
);

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case types.INIT:
      return {
        ...state,
        processId: action.payload.result,
      };
    case types.CREATE_MATERIAL_SUCCESS: {
      const payload = action.payload;
      return dot.set(
        state,
        `entities.processes.${payload.processId}.processMaterials`,
        (list) => [...payload.result, ...list],
      );
    }
    case types.DELETE_MATERIAL_SUCCESS: {
      const payload = action.payload;
      return dot.set(
        state,
        `entities.processes.${action.payload.processId}.processMaterials`,
        (list) => list.filter((id) => id !== payload.materialId),
      );
    }
    case types.CREATE_FINISH_SUCCESS: {
      const payload = action.payload;
      return dot.set(
        state,
        `entities.processes.${payload.processId}.processFinishes`,
        (list) => [...payload.result, ...list],
      );
    }
    case types.DELETE_FINISH_SUCCESS: {
      const payload = action.payload;
      return dot.set(
        state,
        `entities.processes.${payload.processId}.processFinishes`,
        (list) => list.filter((id) => id !== payload.finishId),
      );
    }
    case types.ADD_OPERATIONS_OPEN_MODAL:
      return dot.merge(state, 'addOperations', {
        isModalOpen: true,
        type: action.payload.type,
      });
    case types.ADD_OPERATIONS_CLOSE_MODAL:
      return dot.merge(state, 'addOperations', {
        isModalOpen: false,
      });
    case types.ADD_MATERIALS_OPEN_MODAL:
      return dot.merge(state, 'addMaterials', {
        isModalOpen: true,
      });
    case types.ADD_MATERIALS_CLOSE_MODAL:
      return dot.merge(state, 'addMaterials', {
        isModalOpen: false,
      });
    case types.ADD_MATERIALS_REQUEST:
      return dot.merge(state, 'addMaterials', {
        isSubmitting: true,
      });
    case types.ADD_MATERIALS_SUCCESS:
    case types.ADD_MATERIALS_ERROR:
      return dot.merge(state, 'addAddOns', {
        isSubmitting: false,
      });
    case types.ADD_ADD_ONS_OPEN_MODAL:
      return dot.merge(state, 'addAddOns', {
        isModalOpen: true,
      });
    case types.ADD_ADD_ONS_CLOSE_MODAL:
      return dot.merge(state, 'addAddOns', {
        isModalOpen: false,
      });
    case types.ADD_ADD_ONS_REQUEST:
      return dot.merge(state, 'addAddOns', {
        isSubmitting: true,
      });
    case types.ADD_ADD_ONS_SUCCESS:
    case types.ADD_ADD_ONS_ERROR:
      return dot.merge(state, 'addAddOns', {
        isModalOpen: false,
        isSubmitting: false,
      });
    case types.ADD_PROFIT_ITEMS_OPEN_MODAL:
      return dot.merge(state, 'addProfitItems', {
        isModalOpen: true,
      });
    case types.ADD_PROFIT_ITEMS_CLOSE_MODAL:
      return dot.merge(state, 'addProfitItems', {
        isModalOpen: false,
      });
    case types.ADD_PROFIT_ITEMS_REQUEST:
      return dot.merge(state, 'addProfitItems', {
        isSubmitting: true,
      });
    case types.ADD_PROFIT_ITEMS_SUCCESS:
    case types.ADD_PROFIT_ITEMS_ERROR:
      return dot.merge(state, 'addProfitItems', {
        isModalOpen: false,
        isSubmitting: false,
      });
    case types.ADD_DISCOUNTS_OPEN_MODAL:
      return dot.merge(state, 'addDiscounts', {
        isModalOpen: true,
      });
    case types.ADD_DISCOUNTS_CLOSE_MODAL:
      return dot.merge(state, 'addDiscounts', {
        isModalOpen: false,
      });
    case types.ADD_DISCOUNTS_REQUEST:
      return dot.merge(state, 'addDiscounts', {
        isSubmitting: true,
      });
    case types.ADD_DISCOUNTS_SUCCESS:
    case types.ADD_DISCOUNTS_ERROR:
      return dot.merge(state, 'addDiscounts', {
        isModalOpen: false,
        isSubmitting: false,
      });
    case types.UPDATE_OP_FACTORY_REQUEST: {
      const state1 = dot.merge(state, 'addOperations', { isFetching: true });
      return dot.merge(state1, `entities.processes.${state.processId}`, {
        state,
      });
    }
    case types.UPDATE_OP_FACTORY_SUCCESS:
      return dot.merge(state, 'addOperations', {
        isFetching: false,
        isModalOpen: false,
      });
    case types.UPDATE_OP_FACTORY_ERROR:
      return dot.merge(state, 'addOperations', {
        isModalOpen: false,
        isFetching: false,
      });
    case types.FORMULA_OPEN_MODAL:
      return dot.merge(state, 'formulaEditor', {
        isModalOpen: true,
      });
    case types.FORMULA_CLOSE_MODAL:
      return dot.merge(state, 'formulaEditor', {
        isModalOpen: false,
        message: null,
        error: false,
      });
    case types.FORMULA_COMPILE_REQUEST:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: true,
      });
    case types.FORMULA_COMPILE_SUCCESS:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: false,
        message: FORMULA_COMPILE_SUCCESS_MSG,
        error: false,
      });
    case types.FORMULA_COMPILE_ERROR:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: false,
        message: action.payload.error,
        error: true,
      });
    case types.FORMULA_UPDATE_REQUEST:
      return dot.merge(state, 'formulaEditor', {
        isUpdating: true,
      });
    case types.FORMULA_UPDATE_SUCCESS: {
      let nextState = state;
      nextState = dot.merge(
        nextState,
        `entities.processes.${action.payload.processId}.opFactoryFormula`,
        action.payload.formula,
      );
      const savedMsg = action.payload.formula.revertedFrom
        ? `Restored from: ${formatTimestamp(
            action.payload.formula.revertedFrom,
          )}`
        : 'Saved';
      nextState = dot.merge(nextState, 'formulaEditor', {
        isUpdating: false,
        error: false,
        message: savedMsg,
      });
      return nextState;
    }
    case types.FORMULA_UPDATE_ERROR:
      return dot.merge(state, 'formulaEditor', {
        isUpdating: false,
        message: action.payload.error,
        error: true,
      });
    case types.FORMULA_RESET_MESSAGE:
      return dot.merge(state, 'formulaEditor', {
        message: '',
        error: false,
      });
    case types.LOAD_ADD_ONS_SUCCESS: {
      const nextState = {
        ...state,
        availableAddOnIds: action.payload.result,
      };
      return dot.merge(nextState, 'addAddOns', {
        addOnSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_ADD_ONS_ERROR:
      return dot.merge(state, 'addAddOns', {
        isSearchingAddOns: false,
      });
    case types.SEARCH_ADD_ONS_SUCCESS: {
      const nextAvailableAddOnIds = [
        ...state.availableAddOnIds,
        ...action.payload.result,
      ];
      const nextState = {
        ...state,
        availableAddOnIds: nextAvailableAddOnIds.filter(
          (addOnId, index) => nextAvailableAddOnIds.indexOf(addOnId) === index,
        ),
      };
      return dot.merge(nextState, 'addAddOns', {
        isSearchingAddOns: false,
        addOnSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_ADD_ONS_REQUEST:
      return dot.merge(state, 'addAddOns', {
        isSearchingAddOns: true,
      });

    case types.LOAD_PROFIT_ITEMS_SUCCESS: {
      const nextState = {
        ...state,
        availableProfitItemIds: action.payload.result,
      };
      return dot.merge(nextState, 'addProfitItems', {
        profitItemSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_PROFIT_ITEMS_ERROR:
      return dot.merge(state, 'addProfitItems', {
        isSearchingProfitItems: false,
      });
    case types.SEARCH_PROFIT_ITEMS_SUCCESS: {
      const nextAvailableProfitItemIds = [
        ...state.availableProfitItemIds,
        ...action.payload.result,
      ];
      const nextState = {
        ...state,
        availableProfitItemIds: nextAvailableProfitItemIds.filter(
          (profitItemId, index) =>
            nextAvailableProfitItemIds.indexOf(profitItemId) === index,
        ),
      };
      return dot.merge(nextState, 'addProfitItems', {
        isSearchingProfitItems: false,
        profitItemSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_PROFIT_ITEMS_REQUEST:
      return dot.merge(state, 'addProfitItems', {
        isSearchingProfitItems: true,
      });
    case types.LOAD_DISCOUNTS_SUCCESS: {
      const nextState = {
        ...state,
        availableDiscountIds: action.payload.result,
      };
      return dot.merge(nextState, 'addDiscounts', {
        discountSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_DISCOUNTS_ERROR:
      return dot.merge(state, 'addDiscounts', {
        isSearchingDiscounts: false,
      });
    case types.SEARCH_DISCOUNTS_SUCCESS: {
      const nextAvailableDiscountIds = [
        ...state.availableDiscountIds,
        ...action.payload.result,
      ];
      const nextState = {
        ...state,
        availableDiscountIds: nextAvailableDiscountIds.filter(
          (discountId, index) =>
            nextAvailableDiscountIds.indexOf(discountId) === index,
        ),
      };
      return dot.merge(nextState, 'addDiscounts', {
        isSearchingDiscounts: false,
        discountSearchResultsIds: action.payload.result,
      });
    }
    case types.SEARCH_DISCOUNTS_REQUEST:
      return dot.merge(state, 'addDiscounts', {
        isSearchingDiscounts: true,
      });
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

export const getScope = (state) => state.modules.processes.detailsPage;

export const getIsAddMaterialsSubmitting = (state) =>
  getScope(state).addMaterials.isFetching;

export const getIsAddAddOnsSubmitting = (state) =>
  getScope(state).addAddOns.isSubmitting;

export const getIsAddAddOnsModalOpen = (state) =>
  getScope(state).addAddOns.isModalOpen;

export const getIsSearchingAddOns = (state) =>
  getScope(state).addAddOns.isSearchingAddOns;

export const getIsAddProfitItemsSubmitting = (state) =>
  getScope(state).addProfitItems.isSubmitting;

export const getIsAddProfitItemsModalOpen = (state) =>
  getScope(state).addProfitItems.isModalOpen;

export const getIsSearchingProfitItems = (state) =>
  getScope(state).addProfitItems.isSearchingProfitItems;

export const getIsAddDiscountsSubmitting = (state) =>
  getScope(state).addDiscounts.isSubmitting;

export const getIsAddDiscountsModalOpen = (state) =>
  getScope(state).addDiscounts.isModalOpen;

export const getIsSearchingDiscounts = (state) =>
  getScope(state).addDiscounts.isSearchingDiscounts;

export const getProcess = (state) => {
  const entities = getScope(state).entities;
  const opDefEntities = opDefSelectors.getScope(state).entities;
  const process = denormalize(getScope(state).processId, processSchema, {
    ...entities,
    ...opDefEntities,
  });
  return process;
};

export const getAvailableAddOns = (state) =>
  denormalize(
    getScope(state).availableAddOnIds,
    [addOnSchema],
    getScope(state).entities,
  );

export const getAddOnSearchResults = (state) =>
  denormalize(
    getScope(state).addAddOns.addOnSearchResultsIds,
    [addOnSchema],
    getScope(state).entities,
  );

export const getAvailableProfitItems = (state) =>
  denormalize(
    getScope(state).availableProfitItemIds,
    [profitItemSchema],
    getScope(state).entities,
  );

export const getProfitItemSearchResults = (state) =>
  denormalize(
    getScope(state).addProfitItems.profitItemSearchResultsIds,
    [profitItemSchema],
    getScope(state).entities,
  );

export const getAvailableDiscounts = (state) =>
  denormalize(
    getScope(state).availableDiscountIds,
    [discountSchema],
    getScope(state).entities,
  );

export const getDiscountSearchResults = (state) =>
  denormalize(
    getScope(state).addDiscounts.discountSearchResultsIds,
    [discountSchema],
    getScope(state).entities,
  );

export const getOpFactory = (state) => getProcess(state).opFactory;

export const getOpDef = (state) => {
  const process = getProcess(state);
  if (
    process.opFactoryClassName === entities.OP_FACTORY_CLASS_NAMES.MARKETPLACE
  ) {
    return opDefSelectors.getOpDefById(state, process.opFactory.opDefId);
  }
  return null;
};

export const getOpDefs = (state) => {
  const process = getProcess(state);
  if (
    process.opFactoryClassName === entities.OP_FACTORY_CLASS_NAMES.MARKETPLACE
  ) {
    return [getOpDef(state)];
  } else {
    const { opDefTemplates } = getOpFactory(state);
    const opDefs = opDefSelectors.getOpDefs(state);
    return opDefTemplates.map((x) => opDefs.find(propEq('id', x.opDefId)));
  }
};

export const getProcessMaterial = (state, materialId) =>
  denormalize(
    materialId,
    entities.processMaterialSchema,
    getScope(state).entities,
  );

export const getProcessFinish = (state, finishId) =>
  denormalize(finishId, entities.processFinishSchema, getScope(state).entities);

export const getError = () => null;

export const getOpDefListByType = (state, type) => {
  const { finishOpDefIds, opFactory } = getProcess(state);
  const opDefs = opDefSelectors.getOpDefs(state);
  if (type === 'operations')
    return opFactory.opDefTemplates.map((opDefTemplate) => ({
      opDef: opDefs.find(propEq('id', opDefTemplate.opDefId)),
      opDefTemplate,
    }));
  else if (type === 'finishes')
    return finishOpDefIds
      .map((opDefId) => opDefs.find(propEq('id', opDefId)))
      .map((opDef) => ({ opDef, opDefTemplate: null }));
};

export const getProcessId = (state) => getScope(state).processId;

export const getFormulaEditorState = (state) => getScope(state).formulaEditor;
